import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { TrendingUp, DollarSign, Target, BarChart3, Users } from 'lucide-react';

export const VentureSettings = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState({
    deal_notifications: true,
    portfolio_tracking: true,
    investment_alerts: true,
    auto_screening: false,
    ticket_size_min: 50000,
    ticket_size_max: 500000,
    investment_stage: ['seed', 'series-a'],
    geographic_focus: ['north-america'],
    sector_focus: ['sports-tech', 'wellness'],
  });

  const updateSetting = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Investment Criteria
          </CardTitle>
          <CardDescription>
            Define your investment preferences and screening criteria.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Minimum ticket size</Label>
              <Input
                type="number"
                value={settings.ticket_size_min}
                onChange={(e) => updateSetting('ticket_size_min', parseInt(e.target.value))}
                placeholder="50000"
              />
            </div>
            <div className="space-y-2">
              <Label>Maximum ticket size</Label>
              <Input
                type="number"
                value={settings.ticket_size_max}
                onChange={(e) => updateSetting('ticket_size_max', parseInt(e.target.value))}
                placeholder="500000"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Investment stages</Label>
            <div className="grid grid-cols-2 gap-2">
              {['pre-seed', 'seed', 'series-a', 'series-b', 'growth'].map((stage) => (
                <div key={stage} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={stage}
                    checked={settings.investment_stage.includes(stage)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateSetting('investment_stage', [...settings.investment_stage, stage]);
                      } else {
                        updateSetting('investment_stage', settings.investment_stage.filter(s => s !== stage));
                      }
                    }}
                    className="rounded"
                  />
                  <Label htmlFor={stage} className="capitalize">{stage.replace('-', ' ')}</Label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="auto-screening">Auto-screen opportunities</Label>
            <Switch
              id="auto-screening"
              checked={settings.auto_screening}
              onCheckedChange={(checked) => updateSetting('auto_screening', checked)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Portfolio Management
          </CardTitle>
          <CardDescription>
            Configure portfolio tracking and reporting preferences.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="portfolio-tracking">Enable portfolio tracking</Label>
            <Switch
              id="portfolio-tracking"
              checked={settings.portfolio_tracking}
              onCheckedChange={(checked) => updateSetting('portfolio_tracking', checked)}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="investment-alerts">Investment milestone alerts</Label>
            <Switch
              id="investment-alerts"
              checked={settings.investment_alerts}
              onCheckedChange={(checked) => updateSetting('investment_alerts', checked)}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Reporting frequency</Label>
            <Select defaultValue="monthly">
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Geographic & Sector Focus
          </CardTitle>
          <CardDescription>
            Define your geographic and sector investment preferences.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Geographic focus</Label>
            <div className="grid grid-cols-2 gap-2">
              {['north-america', 'europe', 'asia-pacific', 'global'].map((region) => (
                <div key={region} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={region}
                    checked={settings.geographic_focus.includes(region)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateSetting('geographic_focus', [...settings.geographic_focus, region]);
                      } else {
                        updateSetting('geographic_focus', settings.geographic_focus.filter(r => r !== region));
                      }
                    }}
                    className="rounded"
                  />
                  <Label htmlFor={region} className="capitalize">{region.replace('-', ' ')}</Label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Sector focus</Label>
            <div className="grid grid-cols-2 gap-2">
              {['sports-tech', 'wellness', 'fitness', 'entertainment', 'media', 'e-commerce'].map((sector) => (
                <div key={sector} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={sector}
                    checked={settings.sector_focus.includes(sector)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateSetting('sector_focus', [...settings.sector_focus, sector]);
                      } else {
                        updateSetting('sector_focus', settings.sector_focus.filter(s => s !== sector));
                      }
                    }}
                    className="rounded"
                  />
                  <Label htmlFor={sector} className="capitalize">{sector.replace('-', ' ')}</Label>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Fund Information
          </CardTitle>
          <CardDescription>
            Manage fund details and investment thesis.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Investment thesis</Label>
            <Textarea
              placeholder="Describe your investment focus and strategy..."
              className="min-h-[100px]"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Decision timeline (days)</Label>
            <Select defaultValue="30">
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="14">14 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Button className="w-full">
        Save Venture Settings
      </Button>
    </div>
  );
};